/**
 * Contextual URL helpers.
 *
 * Kronos URLs carry the active instance (and, when it differs from the
 * instance's active edition, the edition) as a path prefix:
 *
 *   /i/:slug                       instance, active edition (edition omitted)
 *   /i/:slug/e/:editionId          instance, explicit (non-active) edition
 *
 * These segments are exposed to React Router through a dynamic `basename`
 * (see index.js). React Router then strips the prefix from
 * `useLocation().pathname` and re-adds it to every `<Link>`/`navigate`, so the
 * rest of the app keeps working with plain logical paths (`/screenings`, ...).
 *
 * Public (unauthenticated) routes use a separate, non-instance prefix:
 *   /p/:slug/...        (canonical)
 *   /public/:slug/...   (legacy alias, kept for backward compatibility)
 */

export const INSTANCE_SEG = 'i';
export const EDITION_SEG = 'e';
export const PUBLIC_CANONICAL_SEG = 'p';
export const PUBLIC_LEGACY_SEG = 'public';

const current_pathname = () =>
    (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';

/**
 * Parse a raw browser pathname into its instance/edition context and the
 * remaining logical path.
 *
 * @param {string} [pathname]
 * @returns {{ slug: string|null, editionId: string|null, basename: string, logicalPath: string }}
 */
export function parseUrlContext(pathname = current_pathname()) {
    const segments = String(pathname || '').split('/').filter(Boolean);

    if (segments[0] !== INSTANCE_SEG || !segments[1]) {
        const logical = `/${segments.join('/')}`;
        return {
            slug: null,
            editionId: null,
            basename: '',
            logicalPath: logical === '/' ? '/' : logical,
        };
    }

    const slug = decodeURIComponent(segments[1]);
    let idx = 2;
    let editionId = null;
    let basename = `/${INSTANCE_SEG}/${segments[1]}`;

    if (segments[2] === EDITION_SEG && segments[3]) {
        editionId = decodeURIComponent(segments[3]);
        basename += `/${EDITION_SEG}/${segments[3]}`;
        idx = 4;
    }

    const logical = `/${segments.slice(idx).join('/')}`;
    return {
        slug,
        editionId,
        basename,
        logicalPath: logical === '/' ? '/' : logical,
    };
}

/**
 * React Router `basename` for the current (or given) URL. Returns '/' when the
 * URL carries no instance prefix (public pages, bare/legacy URLs).
 *
 * @param {string} [pathname]
 * @returns {string}
 */
export function computeBasename(pathname = current_pathname()) {
    return parseUrlContext(pathname).basename || '/';
}

/**
 * Build the instance/edition path prefix (no trailing slash). Empty when no
 * slug is provided.
 */
export function buildPrefix({ slug, editionId } = {}) {
    if (!slug) {
        return '';
    }
    let prefix = `/${INSTANCE_SEG}/${encodeURIComponent(slug)}`;
    if (editionId) {
        prefix += `/${EDITION_SEG}/${encodeURIComponent(editionId)}`;
    }
    return prefix;
}

/**
 * Build a full contextual URL from a logical path (e.g. `/screenings/edit/x`),
 * preserving an optional search string and hash.
 */
export function buildContextUrl({ slug, editionId } = {}, logicalPath = '/', search = '', hash = '') {
    const prefix = buildPrefix({ slug, editionId });
    let logical = logicalPath || '/';
    if (!logical.startsWith('/')) {
        logical = `/${logical}`;
    }
    const path = logical === '/' ? (prefix || '/') : `${prefix}${logical}`;
    return `${path}${search || ''}${hash || ''}`;
}

/**
 * Edition segment rule: the edition appears in the URL only when it differs
 * from the instance's active edition. Returns the edition id to put in the URL,
 * or null when it should be omitted.
 *
 * @param {string|null} selectedEditionId
 * @param {string|null} activeEditionId  settings.edition for the instance
 */
export function editionSegmentFor(selectedEditionId, activeEditionId) {
    if (!selectedEditionId || !activeEditionId) {
        return null;
    }
    if (String(selectedEditionId) === String(activeEditionId)) {
        return null;
    }
    return String(selectedEditionId);
}

/**
 * Detail actions that pin a document-scoped screen. On instance/edition switch,
 * strip from the first of these through the end of the path.
 */
const DETAIL_ACTIONS = new Set([
    'edit',
    'show',
    'delete',
    'view',
    'duplicate',
    'copy',
    'new',
    'create',
]);

/**
 * True when a path segment looks like a document id (not a static route label).
 * Conservative: ObjectId, UUID, or long numeric ids only.
 *
 * @param {string} segment
 * @returns {boolean}
 */
export function looksLikeResourceId(segment) {
    const seg = String(segment || '');
    if (!seg || DETAIL_ACTIONS.has(seg)) {
        return false;
    }
    if (/^[a-f0-9]{24}$/i.test(seg)) {
        return true;
    }
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(seg)) {
        return true;
    }
    if (/^\d{8,}$/.test(seg)) {
        return true;
    }
    return false;
}

/**
 * Pick a safe logical path after an instance/edition switch.
 *
 * Document ids and document-scoped detail actions do not transfer across
 * contexts. Prefer:
 *  1. the current page when it has no id / create action
 *  2. the parent collection (strip detail segments pinned to a document id)
 *  3. `fallback` (default `/`)
 *
 * Stable keys after edit/show (e.g. `/settings/edit/default/mobile`) are kept:
 * every instance shares the same settings tabs, so stripping to `/settings`
 * would land on an empty shell.
 *
 * @param {string} logicalPath  React Router logical path (no `/i/:slug` prefix)
 * @param {string} [fallback='/']
 * @returns {string}
 */
export function resolveContextSwitchPath(logicalPath, fallback = '/') {
    const fallbackPath = fallback && String(fallback).startsWith('/')
        ? String(fallback)
        : `/${fallback || ''}`;
    const safeFallback = fallbackPath === '/' || fallbackPath.length > 1
        ? (fallbackPath.replace(/\/+$/, '') || '/')
        : '/';

    const raw = String(logicalPath || '').split('?')[0].split('#')[0];
    let segs = raw.split('/').filter(Boolean);
    if (!segs.length) {
        return safeFallback;
    }

    // Strip from a detail action only when it pins a document (next segment is
    // a resource id) or is a create flow (new/create) with no transferable target.
    // Do not strip `/settings/edit/default/...` — `default` is a shared key.
    const action_idx = segs.findIndex((s) => DETAIL_ACTIONS.has(s));
    if (action_idx >= 0) {
        const action = segs[action_idx];
        const next = segs[action_idx + 1];
        const is_create = action === 'new' || action === 'create';
        const next_is_id = next != null && looksLikeResourceId(next);
        if (is_create || next_is_id) {
            segs = segs.slice(0, action_idx);
        }
    }

    // Drop an embedded resource id and everything after it
    // (e.g. /operation/workflows/:id/runs → /operation/workflows).
    const id_idx = segs.findIndex((s) => looksLikeResourceId(s));
    if (id_idx >= 0) {
        segs = segs.slice(0, id_idx);
    }

    if (!segs.length) {
        return safeFallback;
    }
    return `/${segs.join('/')}`;
}

/** True when the pathname targets a public (unauthenticated) route. */
export function isPublicPathname(pathname = current_pathname()) {
    return new RegExp(`^/(${PUBLIC_LEGACY_SEG}|${PUBLIC_CANONICAL_SEG})(/.*)?$`).test(
        String(pathname || '')
    );
}

/**
 * Login entry routes that must remain reachable even when a session is already
 * stored locally (password reset / forgot / email confirmation links).
 * Accepts either a React Router logical path (`/login/reset`) or a raw browser
 * path (`/i/:slug/login/reset`).
 */
export function isLoginEntryPathname(pathname = current_pathname()) {
    const logical = parseUrlContext(pathname).logicalPath;
    return /^\/login\/(reset|forgot|confirm-email)(\/|$)/.test(logical);
}

/**
 * Canonical public URL (outside /i/:slug basename). Use with <a href>, not <Link>,
 * so React Router does not prefix the instance path.
 *
 * @param {string} slug
 * @param {...string} parts e.g. 'application', id or 'workflow', id
 * @returns {string}
 */
export function buildPublicUrl(slug, ...parts) {
    if (!slug)
        return '#';

    const segments = [PUBLIC_CANONICAL_SEG, slug, ...parts]
        .filter((part) => part != null && String(part).length > 0)
        .map((part) => encodeURIComponent(String(part)));

    return `/${segments.join('/')}`;
}
